import { attractionCrudConfig, type Attraction } from '@/config/objects/attractions';
import { CrudListView, type CrudConfig, type CrudListHeaderAction } from '@startupquickstart/frontend';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface AttractionsListProps {
  /**
   * Optional custom config (with modified endpoints, actions, etc.)
   */
  config?: CrudConfig<Attraction>;
  /**
   * Optional default where clause for filtering
   */
  defaultWhere?: Record<string, unknown>;
  /**
   * Optional custom onItemClick handler
   */
  onItemClick?: (attraction: Attraction) => void;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Base URL for navigation, defaults to /attractions
   */
  baseUrl?: string;
  /**
   * Custom header actions (displayed in the header alongside Add/Create buttons)
   */
  headerActions?: CrudListHeaderAction[];
  /**
   * Breadcrumbs for navigation
   */
  breadcrumbs?: Array<{ label: string; href?: string; current?: boolean }>;
  /**
   * Saved list ID from URL params — auto-selects this list on mount
   */
  listId?: string;
}

/**
 * Reusable attractions list component
 * Pass a custom config with modified actions to customize behavior
 */
export function AttractionsList({
  config,
  baseUrl = '/attractions',
  defaultWhere,
  onItemClick,
  className,
  headerActions,
  breadcrumbs,
  listId,
}: AttractionsListProps) {
  const navigate = useNavigate();

  const _onItemClick = useCallback(
    (attraction: Attraction) => {
      if (onItemClick) {
        onItemClick(attraction);
      } else {
        navigate(`/attractions/${attraction.id}`);
      }
    },
    [navigate, onItemClick],
  );

  return (
    <div className={className}>
      <CrudListView
        config={config || attractionCrudConfig}
        baseUrl={baseUrl}
        onItemClick={_onItemClick}
        defaultWhere={defaultWhere}
        headerActions={headerActions}
        breadcrumbs={breadcrumbs}
        getValueFromItem={(attraction: Attraction) => attraction.id}
        getLabelFromItem={(attraction: Attraction) => attraction.name}
        listId={listId}
      />
    </div>
  );
}
